import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import * as styles from './phoneNumberInput.css';

export type PhoneCountryOption = {
  countryName: string;
  dialCode: string;
  flagCode: string;
  flagEmoji: string;
};

type PhoneNumberInputProps = {
  options: Array<PhoneCountryOption>;
  selectedDialCode: string;
  phoneNumber: string;
  testId?: string;
  autoFocus?: boolean;
  onDialCodeChange: (dialCode: string) => void;
  onPhoneNumberChange: (value: string) => void;
  disabled?: boolean;
  inputAriaLabel?: string;
};

const toCountryOptionLabel = (option: PhoneCountryOption): string => {
  return `${option.countryName} ${option.dialCode}`;
};

const toDigitsOnly = (value: string): string => value.replace(/\D+/g, '');
const MAX_PHONE_NUMBER_DIGITS = 14;
const toNormalizedSearchValue = (value: string): string =>
  value.trim().toLowerCase();

export const PhoneNumberInput = ({
  options,
  selectedDialCode,
  phoneNumber,
  testId,
  autoFocus,
  onDialCodeChange,
  onPhoneNumberChange,
  disabled,
  inputAriaLabel = 'Phone number',
}: PhoneNumberInputProps) => {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const selectedOption =
    options.find((option) => option.dialCode === selectedDialCode) ??
    options[0];
  const hasCountryOptions = options.length > 0;

  const filteredOptions = useMemo(() => {
    const normalizedSearchValue = toNormalizedSearchValue(searchValue);

    if (!normalizedSearchValue) {
      return options;
    }

    const normalizedDialCodeSearchValue = normalizedSearchValue.replace(
      '+',
      '',
    );

    return options.filter((option) => {
      const normalizedCountryName = option.countryName.toLowerCase();
      const normalizedFlagCode = option.flagCode.toLowerCase();
      const normalizedDialCode = option.dialCode.replace('+', '');

      return (
        normalizedCountryName.startsWith(normalizedSearchValue) ||
        normalizedFlagCode.startsWith(normalizedSearchValue) ||
        normalizedDialCode.startsWith(normalizedDialCodeSearchValue)
      );
    });
  }, [options, searchValue]);

  const closeSelector = (): void => {
    setIsSelectorOpen(false);
    setSearchValue('');
  };

  const toggleSelector = (): void => {
    if (!hasCountryOptions || disabled) {
      return;
    }

    setIsSelectorOpen((previousIsOpen) => !previousIsOpen);
  };

  const handleCountrySelect = (dialCode: string): void => {
    onDialCodeChange(dialCode);
    closeSelector();
  };

  useEffect(() => {
    if (!isSelectorOpen) {
      return;
    }

    searchInputRef.current?.focus();
  }, [isSelectorOpen]);

  useEffect(() => {
    if (!isSelectorOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) {
        closeSelector();
      }
    };

    const handleEscapeKeyPress = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeSelector();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscapeKeyPress);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscapeKeyPress);
    };
  }, [isSelectorOpen]);

  const handleSearchValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchValue(event.currentTarget.value);
  };
  const selectorButtonTestId = testId
    ? `${testId}-country-selector`
    : undefined;
  const selectorMenuTestId = testId ? `${testId}-country-menu` : undefined;
  const searchInputTestId = testId ? `${testId}-country-search` : undefined;
  const optionsListTestId = testId ? `${testId}-country-options` : undefined;
  const phoneInputTestId = testId ? `${testId}-number-input` : undefined;

  return (
    <div className={styles.root} ref={rootRef} data-testid={testId}>
      <div className={clsx(styles.control, disabled && styles.controlDisabled)}>
        <button
          type="button"
          aria-label={`Select country code${
            selectedOption
              ? `, currently ${selectedOption.countryName} ${selectedOption.dialCode}`
              : ''
          }`}
          aria-haspopup="listbox"
          aria-expanded={isSelectorOpen}
          disabled={disabled}
          data-testid={selectorButtonTestId}
          className={styles.selectorButton}
          onClick={toggleSelector}
        >
          {selectedOption ? (
            <span className={styles.selectorLabel}>
              <span className={styles.flag}>
                <span className={styles.flagGlyph}>
                  {selectedOption.flagEmoji}
                </span>
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(
                  styles.selectorCaret,
                  isSelectorOpen && styles.selectorCaretOpen,
                )}
              />
              <span className={styles.dialCode}>{selectedOption.dialCode}</span>
            </span>
          ) : (
            <span className={styles.selectorLabel}>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(
                  styles.selectorCaret,
                  isSelectorOpen && styles.selectorCaretOpen,
                )}
              />
              <span className={styles.dialCode}>{selectedDialCode}</span>
            </span>
          )}
        </button>

        {isSelectorOpen ? (
          <div className={styles.selectorMenu} data-testid={selectorMenuTestId}>
            <div className={styles.selectorSearchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                aria-label="Search country"
                data-testid={searchInputTestId}
                value={searchValue}
                placeholder="Search country or code"
                className={styles.selectorSearchInput}
                onChange={handleSearchValueChange}
              />
            </div>
            <div
              className={styles.optionsList}
              role="listbox"
              data-testid={optionsListTestId}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.dialCode === selectedDialCode}
                    key={`${option.flagCode}-${option.dialCode}`}
                    data-testid={
                      testId
                        ? `${testId}-country-option-${option.flagCode.toLowerCase()}`
                        : undefined
                    }
                    className={clsx(
                      styles.optionButton,
                      option.dialCode === selectedDialCode &&
                        styles.optionButtonSelected,
                    )}
                    onClick={() => handleCountrySelect(option.dialCode)}
                  >
                    <span className={styles.optionIdentity}>
                      <span className={styles.optionFlag}>
                        <span className={styles.optionFlagGlyph}>
                          {option.flagEmoji}
                        </span>
                      </span>
                      <span className={styles.optionLabel}>
                        {toCountryOptionLabel(option)}
                      </span>
                    </span>
                  </button>
                ))
              ) : (
                <span className={styles.noOptionsMessage}>
                  No countries found
                </span>
              )}
            </div>
          </div>
        ) : null}
        <input
          type="tel"
          aria-label={inputAriaLabel}
          placeholder="Phone Number"
          autoFocus={autoFocus}
          value={phoneNumber}
          disabled={disabled}
          data-testid={phoneInputTestId}
          className={styles.phoneInput}
          inputMode="numeric"
          pattern="[0-9]{0,14}"
          maxLength={MAX_PHONE_NUMBER_DIGITS}
          onChange={(event) =>
            onPhoneNumberChange(
              toDigitsOnly(event.currentTarget.value).slice(
                0,
                MAX_PHONE_NUMBER_DIGITS,
              ),
            )
          }
        />
      </div>
    </div>
  );
};
